export default {
  id: "reports",
  title: "Reports & Monthly Billing",
  section: "Billing & Reports",
  order: 40,
  keywords: "reports monthly billing csv download USOR 148 96 95 consolidated EVV mileage attendance payroll hours projection",
  body: `
## What it is

The Reports page is one screen for every report in the app. The workflow is
always the same: pick a **report type**, set the **date range**, narrow with
**filters**, review on screen, and **Download CSV**. What changes per report
is which filters apply and what the columns mean.

## How it works

- **Date range**: choose Month, Two Weeks, Week, Day, or Custom. In Month
  mode the arrows step month by month — most billing runs live here.
- **Filters**: payer, service code (multi-select), office, caregiver,
  client, and admin — each report shows only the filters that apply to it.
- **Approved shifts only**: every billing report reads Approved shifts.
  If a report looks light, the usual cause is shifts still waiting for
  approval — check the Verification page for the count.
- **CSV**: the download always matches the filters currently on screen.

## The reports, grouped

### Billing
- **Monthly Billing Report By Client** — the workhorse review: every
  approved shift line by line (date, auth number, caregiver, client, code,
  clock times, hours, units, rate, total), grouped by client with subtotals
  and a grand total. Use it to sanity-check a month before invoicing.
- **Timesheet Billing Report** — the state's line-by-line upload file for
  converted DSPD codes. Has its own guide with the special rounding rule.
- **DSPD Consolidated Billing (DSG/DSP/MTP/DSI)**, **PBA Billing**,
  **Consolidated Billing (CMP/SLN/SLH/SEI)**, **All DSPD Billing** — the
  consolidated monthly rollups per program area, formatted the way each
  billing target expects.
- **VR Auth Consolidation** and **Monthly Units** — hour/unit totals
  against authorizations, for spotting overruns and remaining balances.

### Operations
- **Payroll Hours Report** — hours per caregiver for a period (this is the
  "simple" view; the Payroll page does the full payroll math).
- **Attendance Report** — client attendance across shifts.
- **Projection vs. Actual (VR/DSPD)** — scheduled hours versus what was
  actually worked, per client.
- **EVV Report** — the electronic visit verification export in the state
  format: member IDs (DSPD PID or VR ID), employee, service code, begin/end
  times, and the clock-in/out map coordinates captured on caregivers'
  devices.
- **Mileage Report** — every recorded trip in the range, including
  non-transport trips, with a Transporting column. See Payroll & Mileage
  for how payable miles are computed.
- **Unserved Active Clients** — clients holding an active authorization
  with no shifts in the range (services owed but not delivered).
- **Auths with Approved Shifts** — the inverse check: which authorizations
  actually have billable work.

### Quarterly
- **Quarterly Report** and **Quarterly Submission Tracker** — generating
  and tracking DSPD quarterly narrative reports. They have their own guide.

## USOR forms (VR monthly billing)

Monthly VR billing generates the USOR forms — **148** for Life Skills-type
codes, **95** for Job Coaching, **96** for Job Development. The forms pull
each shift's notes **and** its clock-out question answers, then use AI to
draft the summary narrative. Two things to know:

- **Always proofread the AI text** before a form goes out — it drafts, you
  own the content.
- A shift documented only through question answers (no free-text note)
  still counts and still bills.

## Step by step: a clean monthly billing review

1. First business days of the month: open **Verification**, look at last
   month's unverified count, and get shifts approved.
2. Run **Monthly Billing Report By Client** for the month with no filters.
   Skim client subtotals for anything odd (missing days, doubled shifts,
   zero-rate rows).
3. Fix problems at the source (shift times, auth rates), re-run until clean.
4. Run the payer-specific outputs: consolidated DSPD reports, timesheet
   billing files, VR invoices/USOR forms.
5. Download CSVs as you go — each is your record of what was billed.
`,
};
